import { Grid } from "@mui/material";
import { useSnackbar } from "notistack";
import { DragEvent, Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useQuery } from "react-query";
import { AlertDialog } from "../../components/core/Dialogs/AlertDialog";
import http from "../../api/core/api";
import { apiUrl } from "../../api/core/apiUrl";
import { Loader } from "../../components/core/Loader/Loader";
import { useDeleteDialog } from "../../hooks/useDeleteDialog";
import { dictionary } from "../../utils/core/dictionary";
import { Board } from "../../types/app/board.types";
import { BoardContentCard } from "./BoardContentCard";
import { BoardContent } from "../../types/app/boardContent.types";
import { useLocation, useParams } from "react-router-dom";
import { fullscreenPathPrefix, paths } from "../../utils/core/routes";

interface BoardContentAttachment {
  id: number;
  parentId: number;
  name: string;
  contentType: string;
  sizeInBytes: number;
}

interface Props {
  readOnly: boolean;
  board: Board;
}
export function BoardContentList({ board, readOnly }: Props) {
  const { pathname } = useLocation();
  const isFullScreen = pathname.includes(fullscreenPathPrefix);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    isOpen: isDeleteDialogOpen,
    idToDelete,
    setDeleteAttempt,
    cancelDeleteAttempt,
    confirmDeleteAttempt,
  } = useDeleteDialog();

  const [boardContentList, setBoardContentList] = useState<
    BoardContent[] | null
  >();
  const [draggedContent, setDraggedContent] = useState<BoardContent | null>(
    null
  );
  const [dragOverCell, setDragOverCell] = useState<string | null>(null);

  const fetchBoards = async () => {
    const response = await http.apiGet({
      url: `${apiUrl.BOARD_CONTENT_LIST}?parentId=${id}`,
    });
    setBoardContentList(response?.data);
  };

  useEffect(() => {
    fetchBoards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: boardAttachments } = useQuery<BoardContentAttachment[]>(
    ["board-attachments", board.id],
    async () => {
      const response = await http.apiGet({
        url: `${apiUrl.BOARD_CONTENT_FILE_ATTACHMENT_LIST}?boardId=${board.id}`,
      });
      return (response?.data ?? []) as BoardContentAttachment[];
    },
    { enabled: !!board.id, staleTime: 5 * 60 * 1000 }
  );

  const attachmentsByContentId = useMemo(() => {
    const map = new Map<number, BoardContentAttachment[]>();
    (boardAttachments ?? []).forEach((a) => {
      const list = map.get(a.parentId) ?? [];
      list.push(a);
      map.set(a.parentId, list);
    });
    return map;
  }, [boardAttachments]);

  const onRemoveItem = async (idToDelete: number | null) => {
    if (idToDelete === null) return;
    const response = await http.apiDelete({
      url: `${apiUrl.BOARD_CONTENT_DELETE}?id=${idToDelete}`,
    });
    if (response?.status === 204) {
      enqueueSnackbar(dictionary.BOARD_DELETED, {
        variant: "success",
      });
      confirmDeleteAttempt();
      fetchBoards();
    }
  };

  const handleOnDelete = (id: number) => setDeleteAttempt(id);
  const handleOnCreate = (boardContent: BoardContent) =>
    navigate(`${paths.BOARD_DETAIL}/${board.id}${paths.BOARD_CONTENT_CREATE}`, {
      state: boardContent,
    });
  const handleOnEdit = (boardContent: BoardContent) =>
    navigate(
      `${paths.BOARD_DETAIL}/${board.id}${paths.BOARD_CONTENT_EDIT}/${boardContent.id}`,
      { state: boardContent }
    );

  const handleConfirm = () => {
    if (!idToDelete) return;
    onRemoveItem(idToDelete);
  };

  const getContentFromList = (rowIndex: number, colIndex: number) => {
    let defaultCard = {
      id: null,
      boardId: board.id,
      positionRow: rowIndex,
      positionColumn: colIndex,
      headline: "",
      urlPreview: null,
      urlDetail: null,
    } as BoardContent;
    if (!boardContentList) return defaultCard;
    let card = boardContentList.find(
      (content: BoardContent) =>
        content.positionRow === rowIndex && content.positionColumn === colIndex
    );
    return card ? card : defaultCard;
  };

  const cellKey = (row: number, col: number) => `${row}_${col}`;

  const isSourceCell = (row: number, col: number) =>
    draggedContent?.positionRow === row &&
    draggedContent?.positionColumn === col;

  const handleDragStart = (boardContent: BoardContent) =>
    setDraggedContent(boardContent);

  const handleDragEnd = () => {
    setDraggedContent(null);
    setDragOverCell(null);
  };

  const handleDragOver = (e: DragEvent, row: number, col: number) => {
    if (!draggedContent) return;
    // bez preventDefault prohlížeč puštění na buňku nedovolí
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const key = cellKey(row, col);
    if (dragOverCell !== key) setDragOverCell(key);
  };

  const handleDragLeave = (e: DragEvent, row: number, col: number) => {
    // dragleave chodí i při přejezdu na potomka buňky, ten ignorujeme
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    if (dragOverCell === cellKey(row, col)) setDragOverCell(null);
  };

  // Přesun na prázdnou pozici, nebo prohození s obsazenou pozicí
  const handleDrop = async (e: DragEvent, row: number, col: number) => {
    e.preventDefault();
    const source = draggedContent;
    handleDragEnd();
    if (
      !source?.id ||
      (source.positionRow === row && source.positionColumn === col)
    )
      return;
    const response = await http.apiPut({
      url: apiUrl.BOARD_CONTENT_MOVE,
      data: { id: source.id, targetRow: row, targetColumn: col },
    });
    if (response?.status === 204) {
      enqueueSnackbar(dictionary.BOARD_CONTENT_MOVED, { variant: "success" });
    } else {
      enqueueSnackbar(dictionary.BOARD_CONTENT_MOVE_FAILED, {
        variant: "error",
      });
    }
    fetchBoards();
  };

  if (!boardContentList) return <Loader />;
  return (
    <>
      <Grid
        container
        className={draggedContent ? "board-dragging" : undefined}
        sx={{
          height: isFullScreen
            ? `calc((100vh - 95px) / ${board.rows})`
            : `calc((100vh - 170px) / ${board.rows})`,
        }}
      >
        {/* výpis boardů podle indexu řádku a sloupce */}
        {[...Array(board.rows)].map((key, row) => (
          <Fragment key={row}>
            {[...Array(board.cols)].map((key, col) => (
              <Grid
                item
                xs={12 / board.cols}
                key={"col" + col + "_row" + row}
                className={
                  dragOverCell === cellKey(row, col) && !isSourceCell(row, col)
                    ? "board-cell-drop-target"
                    : isSourceCell(row, col)
                    ? "board-cell-drag-source"
                    : undefined
                }
                onDragOver={
                  readOnly ? undefined : (e) => handleDragOver(e, row, col)
                }
                onDragLeave={
                  readOnly ? undefined : (e) => handleDragLeave(e, row, col)
                }
                onDrop={readOnly ? undefined : (e) => handleDrop(e, row, col)}
              >
                <BoardContentCard
                  readOnly={readOnly}
                  key={"col" + col + "_row" + row}
                  board={board}
                  boardContent={getContentFromList(row, col)}
                  attachments={
                    attachmentsByContentId.get(
                      getContentFromList(row, col).id ?? -1
                    ) ?? []
                  }
                  handleOnCreate={handleOnCreate}
                  handleOnEdit={handleOnEdit}
                  handleOnDelete={handleOnDelete}
                  handleOnDragStart={handleDragStart}
                  handleOnDragEnd={handleDragEnd}
                />
              </Grid>
            ))}
          </Fragment>
        ))}
      </Grid>
      <AlertDialog
        open={isDeleteDialogOpen}
        title={dictionary.DELETE}
        content={dictionary.DELETE_MESSAGE}
        handleClose={cancelDeleteAttempt}
        handleConfirm={handleConfirm}
      />
    </>
  );
}
